import { useMediaQuery } from '@jouzen/ecom-components/hooks/useMediaQuery';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { useReducedMotion } from 'framer-motion';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

const BOTTOM_OFFSET = 60;

export interface UseScroller {
  sectionRefs: RefObject<Record<string, HTMLElement>>;
}

export const useScroller = (
  currentStepId: number,
  prevStep: RefObject<number>,
): UseScroller => {
  const sectionRefs = useRef<Record<string, HTMLElement>>({});
  const prefersReducedMotion = useReducedMotion();
  const isLargeBreakpoint = useMediaQuery(
    `(min-width: ${BREAKPOINTS.large}px)`,
  );
  const yOffset = isLargeBreakpoint ? 96 : 40;

  useEffect(() => {
    if (currentStepId !== prevStep.current) {
      // scroll to section
      const nextElement = sectionRefs.current[currentStepId];
      if (nextElement) {
        /**
         * requestAnimationFrame ensures that the scrollTargetPosition is calculated after css animations (like the one that
         * changes the height of the size option container when the 'select your size' option expands and retracts) have run,
         * and the layout is up-to-date
         */
        // Memoize refKeys and lastKey before animation frame
        const refKeys = Object.keys(sectionRefs.current);
        const lastKey = refKeys[refKeys.length - 1];
        requestAnimationFrame(() => {
          const rect = nextElement.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;
          const elementHeight = rect.height;
          const windowHeight = window.innerHeight;
          // Top position: element aligns with top of viewport, offset by yOffset
          const topPosition = elementTop - yOffset;
          // BOTTOM_OFFSET px from bottom position (for last element)
          const bottomPosition =
            elementTop + elementHeight - windowHeight + BOTTOM_OFFSET;

          // Direct comparison for last key
          const isLast = String(currentStepId) === lastKey;
          const scrollTargetPosition = isLast ? bottomPosition : topPosition;

          window.scrollTo({
            top: scrollTargetPosition,
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
          });
        });
      }
    }
    prevStep.current = currentStepId;
  }, [currentStepId, prefersReducedMotion, prevStep, yOffset]);

  return { sectionRefs };
};
